import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { Plus, Edit, Trash2, Target } from "lucide-react";
import * as Icons from "lucide-react";
import { Meta } from "@/types/database";
import { EmptyState } from "../ui/EmptyState";

interface GoalManagerProps {
  onGoalUpdate?: () => void;
}

const iconOptions = [
  { value: "Target", label: "Alvo" },
  { value: "Home", label: "Casa" },
  { value: "Car", label: "Carro" },
  { value: "Plane", label: "Viagem" },
  { value: "GraduationCap", label: "Educação" },
  { value: "Heart", label: "Saúde" },
  { value: "Gamepad2", label: "Entretenimento" },
  { value: "ShoppingBag", label: "Compras" },
  { value: "Briefcase", label: "Trabalho" },
  { value: "Gift", label: "Presente" },
];

export function GoalManager({ onGoalUpdate }: GoalManagerProps) {
  const [goals, setGoals] = useState<Meta[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Meta | null>(null);
  const [formData, setFormData] = useState({
    titulo: "",
    valor_meta: "",
    valor_atual: "",
    prazo_meses: "",
    icone: "Target",
    meta_principal: false,
  });
  ;
  const { user, loading: authLoading } = useAuth();

  const formatCurrency = (value: string | number) => {
    const numValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numValue);
  }

  const fetchGoals = async () => {
    try {
      const { data, error } = await supabase
        .from('metas')
        .select('*')
        .order("meta_principal", { ascending: false })
        .order("criado_em", { ascending: false });

      if (error) throw error;
      setGoals(data || []);
    } catch (error) {
      console.error('Error fetching goals:', error);
      toast.error("Não foi possível carregar as metas.");
    }
  };

  useEffect(() => {
    fetchGoals();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    if (!user) {
      toast.error("Usuário não autenticado.");
      setSaving(false);
      return;
    }

    try {
      if (formData.meta_principal) {
        if (editingGoal) {
          // When updating existing goal, exclude current goal from update
          await supabase
            .from('metas')
            .update({ meta_principal: false })
            .neq('id', editingGoal.id);
        } else {
          // When creating new goal, remove main flag from all existing goals
          await supabase
            .from('metas')
            .update({ meta_principal: false })
            .eq('meta_principal', true);
        }
      }

      const goalData = {
        titulo: formData.titulo,
        valor_meta: parseFloat(formData.valor_meta) || 0,
        valor_atual: parseFloat(formData.valor_atual) || 0,
        prazo_meses: parseInt(formData.prazo_meses) || 0,
        icone: formData.icone,
        meta_principal: formData.meta_principal,
        user_id: user.id,
      };

      if (editingGoal) {
        const { error } = await supabase
          .from('metas')
          .update(goalData)
          .eq('id', editingGoal.id);

        if (error) throw error;

        toast.success("Meta atualizada com sucesso!");
      } else {
        const { error } = await supabase
          .from('metas')
          .insert([goalData]);

        if (error) throw error;

        toast.success("Meta criada com sucesso!");
      }

      handleCloseModal();
      fetchGoals();
      onGoalUpdate?.();
    } catch (error) {
      console.error('Error saving goal:', error);
      toast.error("Não foi possível salvar a meta.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('metas')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success("Meta excluída com sucesso!");

      fetchGoals();
      onGoalUpdate?.();
    } catch (error) {
      console.error('Error deleting goal:', error);
      toast.error("Não foi possível excluir a meta.");
    }
  };

  const handleEdit = (goal: Meta) => {
    setEditingGoal(goal);
    setFormData({
      titulo: goal.titulo,
      valor_meta: goal.valor_meta.toString(),
      valor_atual: goal.valor_atual.toString(),
      prazo_meses: goal.prazo_meses.toString(),
      icone: goal.icone,
      meta_principal: goal.meta_principal,
    });
    setDialogOpen(true);
  };

  const handleCloseModal = () => {
    setDialogOpen(false);
    setEditingGoal(null);
    setFormData({
      titulo: "",
      valor_meta: "",
      valor_atual: "",
      prazo_meses: "",
      icone: "Target",
      meta_principal: false,
    });
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 90) return "bg-green-500";
    if (progress >= 70) return "bg-purple-500";
    if (progress >= 50) return "bg-yellow-500";
    return "bg-red-500";
  };

  if (authLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="text-muted-foreground">Carregando...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!user) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="text-muted-foreground">Usuário não autenticado</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <Dialog open={dialogOpen} onOpenChange={(open) => !open && handleCloseModal()}>
            <DialogTrigger asChild>
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Nova Meta
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>
                  {editingGoal ? "Editar Meta" : "Nova Meta"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="titulo">Título</Label>
                  <Input
                    id="titulo"
                    placeholder="Digite o título da meta"
                    value={formData.titulo}
                    onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="valor_atual">Valor Atual (R$)</Label>
                    <CurrencyInput
                      id="valor_atual"
                      placeholder="0,00"
                      value={String(formData.valor_atual)}
                      onChange={(valor) => setFormData({ ...formData, valor_atual: String(valor) })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="valor_meta">Valor da Meta (R$)</Label>
                    <CurrencyInput
                      id="valor_meta"
                      placeholder="1000,00"
                      value={String(formData.valor_meta)}
                      onChange={(valor) => setFormData({ ...formData, valor_meta: String(valor) })}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="prazo_meses">Prazo (meses)</Label>
                  <Input
                    id="prazo_meses"
                    type="number"
                    min="1"
                    placeholder="12"
                    value={formData.prazo_meses}
                    onChange={(e) => setFormData({ ...formData, prazo_meses: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="icone">Ícone</Label>
                  <Select value={formData.icone} onValueChange={(value) => setFormData({ ...formData, icone: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {iconOptions.map((option) => {
                        const Icon = (Icons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[option.value];
                        return (
                          <SelectItem key={option.value} value={option.value}>
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              {option.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="meta_principal"
                    checked={formData.meta_principal}
                    onCheckedChange={(checked) => setFormData({ ...formData, meta_principal: checked })}
                  />
                  <Label htmlFor="meta_principal">Meta principal</Label>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={handleCloseModal} className="flex-1">
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={saving} className="flex-1">
                    {saving ? "Salvando..." : "Salvar"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {goals.length === 0 ? (
            <EmptyState
              icon={Target}
              title="Nenhuma meta"
              description="Você ainda não definiu nenhuma meta financeira. Que tal planejar seu próximo sonho agora?"
            />
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {goals.map((goal) => {
                const progress = (goal.valor_atual / goal.valor_meta) * 100;
                const remaining = goal.valor_meta - goal.valor_atual;
                const IconComponent = (Icons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[goal.icone] || Icons.Target;

                return (
                  <div
                    key={goal.id}
                    className="p-4 border border-border rounded-lg hover:bg-accent/30 transition-all duration-300 hover:shadow-medium space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <IconComponent className="h-6 w-6 text-primary" />
                        <div>
                          <span className="font-medium">{goal.titulo}</span>
                          {goal.meta_principal && (
                            <Badge variant="default" className="ml-2 text-xs">
                              Principal
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(goal)}
                          aria-label={`Editar meta ${goal.titulo}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label={`Excluir meta ${goal.titulo}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                              <AlertDialogDescription>
                                Tem certeza que deseja excluir a meta "{goal.titulo}"? Esta ação não pode ser desfeita.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(goal.id)}>
                                Excluir
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          {formatCurrency(goal.valor_atual)} / {formatCurrency(goal.valor_meta)}
                        </span>
                        <span className="font-medium text-sm">
                          {progress.toFixed(1)}%
                        </span>
                      </div>
                      <div className="relative">
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className={`h-3 rounded-full transition-all duration-300 ${getProgressColor(progress)}`}
                            style={{ width: `${Math.min(progress, 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Faltam R$ {remaining.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} • {' '}
                      {goal.prazo_meses} {goal.prazo_meses === 1 ? 'mês' : 'meses'} restante{goal.prazo_meses !== 1 ? 's' : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}